import {
  useEvalFolderExpr,
  useInputsWrite,
  useRead
} from '@features/st-folder-viewer/st-folder-hooks'
import { ReadWriteProvider } from '@st/react-util/form-binding'
import { Title } from '@st/react-util/title'
import { Organization } from '@st/sdk'
import { FullPageCard, Spinner } from '@st/theme'
import { match } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { STQuestionnairePage } from './st-questionnaire-page'
import { ClientOrganizerDashboard } from './client-organizer-dashboard'
import { ClientMfaGate, ClientMfaGateChecking } from './client-mfa-gate'
import { selFolderState } from '@features/st-folder-viewer/st-folder-selectors'

type Props = { folderId: string; preview?: boolean }
export function STQuestionnaire({ preview = false }: Props) {
  const stFolder = useProcess('stFolder')
  const folderState = useSelect('stFolder', selFolderState)
  const organization = folderState?.organization
  const mfaGate = useSelect('stFolder', (s) => {
    if (s.status != 'ready') return undefined
    if (s.questionnaire.status != 'ready') return undefined
    return s.questionnaire.mfaGate
  })

  if (!folderState || !organization || !mfaGate) {
    return <QuestionnaireLoadingCard />
  }

  const content = match({ preview, mfaGate })
    .with({ preview: false, mfaGate: { status: 'locked' } }, ({ mfaGate }) => (
      <ClientMfaGate
        organization={organization}
        returnType={folderState.folder.type}
        last4={mfaGate.last4}
        showError={mfaGate.attempted}
        onLast4Changed={(value) =>
          stFolder.send({ type: 'questionnaire', message: { type: 'mfaLast4Changed', value } })
        }
        onContinue={() =>
          stFolder.send({ type: 'questionnaire', message: { type: 'mfaContinueClicked' } })
        }
      />
    ))
    .with({ preview: false, mfaGate: { status: 'checking' } }, () => (
      <ClientMfaGateChecking organization={organization} />
    ))
    .otherwise(() => <QuestionnaireViewerInner organization={organization} />)

  return (
    <>
      <Title>{organization.name}</Title>
      {content}
    </>
  )
}

function QuestionnaireLoadingCard() {
  return (
    <FullPageCard>
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <Spinner className="size-12 fill-blue-500 text-blue-100" />
        <span>Loading...</span>
      </div>
    </FullPageCard>
  )
}

function QuestionnaireViewerInner({ organization }: { organization: Organization }) {
  const selectedMode = useSelect('stFolder', (s) => {
    if (s.status != 'ready') return undefined
    if (s.questionnaire.status != 'ready') return undefined
    return s.questionnaire.selectedMode
  })

  if (!selectedMode) {
    return null
  }

  return (
    <ReadWriteProvider useRead={useRead} useWrite={useInputsWrite} useEvalExpr={useEvalFolderExpr}>
      {match(selectedMode)
        .with('dashboard', () => <ClientOrganizerDashboard />)
        .with('questionnaire', () => <STQuestionnairePage />)
        .exhaustive()}
    </ReadWriteProvider>
  )
}
